import { Sparkles, CheckCircle2, ArrowRight, Heart, Users, Star } from "lucide-react";

const benefits = [
  {
    icon: Heart,
    title: "Referral Partners Feel Valued",
    text: "Strengthened relationships with referring doctors and partners — a night they'll talk about for years.",
  },
  {
    icon: Users,
    title: "Your Team Feels Seen",
    text: "Customized moment of recognition for the team that makes everything possible.",
  },
  {
    icon: Star,
    title: "Your Practice Looks Exceptional",
    text: "A high-energy, no-dull-moment experience that reflects the excellence of your practice.",
  },
];

export default function FeatureCauseForApplause() {
  return (
    <section
      id="cause-for-applause"
      data-testid="cfa-feature-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-[#262626] overflow-hidden"
    >
      {/* Pink corner accent */}
      <div className="absolute top-0 right-0 w-1/3 h-1 bg-[#EC1985]" />
      <div className="absolute top-0 right-0 w-1 h-32 bg-[#EC1985]" />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Eyebrow tag */}
        <div className="inline-flex items-center gap-2 bg-[#EC1985] text-white px-4 py-2 mb-8">
          <Sparkles size={14} />
          <span className="text-xs uppercase tracking-[0.25em] font-bold">
            Signature Program · Most Booked
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: Copy */}
          <div className="lg:col-span-7">
            <h2
              data-testid="cfa-heading"
              className="h-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white"
            >
              Cause for<br />
              <span className="text-[#EC1985]">Applause.</span>
            </h2>
            <div className="mt-4 text-lg md:text-xl text-white/85 font-bold uppercase tracking-[0.15em]">
              The Ultimate Appreciation Experience
            </div>
            <div className="mt-2 text-sm text-white/60 uppercase tracking-[0.2em] font-bold">
              For Practices · Partners · Corporate Teams · Associations
            </div>

            <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl">
              Ready to host an event your partners — and your team — will
              never forget? This isn't a "thank you" dinner. It's a full-scale
              experience that blends appreciation, humor, and meaningful connection
              into one unforgettable night. Originally built for dental specialists,
              now adapted for corporate, association, and partner-network events.
            </p>

            <div className="mt-10 space-y-5">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    data-testid={`cfa-benefit-${i}`}
                    className="flex gap-5 items-start"
                  >
                    <div className="flex-shrink-0 bg-[#EC1985]/15 border border-[#EC1985]/40 p-3">
                      <Icon size={22} className="text-[#EC1985]" />
                    </div>
                    <div>
                      <div className="font-display text-xl md:text-2xl text-white">
                        {b.title}
                      </div>
                      <p className="text-white/70 mt-1 leading-relaxed">{b.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pull quote */}
            <div className="mt-10 border-l-4 border-[#EC1985] pl-6 py-2 max-w-2xl">
              <p className="font-display text-2xl md:text-3xl text-white leading-tight">
                "Our attendees refused to leave during the break — afraid of missing something."
              </p>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-white/60 font-bold">
                — Dr. William R. Hatcher · Hatcher Orthodontics
              </div>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a
                href="#book"
                data-testid="cfa-book-btn"
                className="btn-primary"
              >
                Host a Cause for Applause Event <ArrowRight size={18} />
              </a>
              <a
                href="mailto:Cherisse@GaryZelesky.com?subject=Cause%20for%20Applause%20-%20Booking%20Inquiry"
                data-testid="cfa-email-btn"
                className="btn-ghost"
              >
                Email Cherisse Directly
              </a>
            </div>

            <div className="mt-6 text-xs uppercase tracking-[0.15em] text-white/50 font-bold">
              ✓ Easy to Book · ✓ Done-For-You Experience · ✓ Customized to Your Practice
            </div>
          </div>

          {/* Right: Image */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <img
                src="/images/gary-banquet-closeup.jpg"
                alt="Gary at a referring doctor appreciation banquet"
                className="w-full h-[520px] md:h-[640px] object-cover border border-[#262626]"
              />
              {/* Floating stat badge */}
              <div className="absolute -bottom-6 -left-6 bg-[#EC1985] text-white p-5 max-w-[280px] hidden md:block">
                <div className="font-display text-4xl leading-none">300+</div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] font-bold">
                  Attendees · Booked 5×+<br />
                  <span className="text-white/80">OMSC of Minnesota</span>
                </div>
              </div>
              {/* Floating bottom-right "Best year ever" */}
              <div className="absolute -top-6 -right-6 bg-black border border-[#EC1985] p-5 max-w-[260px] hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#EC1985] font-bold">
                  Documented Outcome
                </div>
                <div className="font-display text-xl text-white leading-tight mt-2">
                  Largest Oral Surgery practice in Minnesota — repeat client
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">
                  — OMSC of Minnesota
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
